// A20 phase 1b (B6) — THE TWO THINGS AN OPERATOR CAN ACTUALLY DO TO A TICKET.
//
// Migration 41 built a whole lifecycle — a validated state machine, an audit
// trail, a bounded GitHub retry and an explicit escalation — and the console
// exposed NONE of it. `pages/support/api.ts` had three reads and zero mutations;
// TicketDetail rendered the status as a read-only pill;
// allowed_ticket_transitions() had no caller anywhere in either repo. Everything
// below the database was reachable only by hand-crafting an authenticated HTTP
// request, which meant in practice that:
//
//   · every ticket stayed 'open' forever, so migration 36's retention tier
//     (derived from status) could never fire;
//   · github-issues.service.ts's standing instruction — "Customer-portal
//     tickets are not auto-filed; escalate from the console after triage" —
//     named a console action that did not exist.
//
// ── THE ONE RULE THIS FILE FOLLOWS THROUGHOUT ───────────────────────────────
// THE DATABASE IS THE ENFORCER; THIS IS AN EXPLANATION. Every control here is
// driven by what the server said (`allowed_transitions`, read from migration
// 41's own function) and every refusal it pre-empts is ALSO enforced in the RPC.
// Nothing here is a security boundary and nothing here re-implements the rules:
// a second copy of a state machine is how the buttons and the database drift
// into disagreeing, and the operator collects a 409 for a move the UI offered.
// Where a control disables itself, it does so to EXPLAIN the refusal before the
// click rather than to be the refusal.

import React, { useCallback, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ExternalLink, GitBranch, Github, Lock, RefreshCw, ShieldAlert, Timer,
} from 'lucide-react';
import { formatDateTime, formatRelativeTime } from '@/lib/utils';
import { Pill } from '@/components/shared/badges';
import { Card, CardHead } from '@/components/shared/primitives';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { useToast } from '@/components/shared/ToastProvider';
import { GithubSyncChip, StatusPill, statusLabel } from './chips';
import {
  setTicketStatus, rearmGithubSync, type SupportTicketDetail, type TicketStatus,
} from './api';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px',
  borderRadius: 8, background: 'var(--surface-raised)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

const noteBox = (bg: string, border: string, fg: string): React.CSSProperties => ({
  padding: '10px 14px', borderRadius: 'var(--radius-md)', background: bg,
  border: `1px solid ${border}`, color: fg, fontSize: 12.5, lineHeight: 1.5,
});

/** The failure of the LAST mutation, kept on screen after its toast has gone. */
function MutationError({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div role="alert" style={{ ...noteBox('#FEE2E2', '#FECACA', '#7F1D1D'), display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <AlertTriangle size={15} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
      {/* VERBATIM. The refusals raised by set_ticket_status() and
          rearm_github_sync() are written for a human — they name the ticket, the
          state it is in and the moves that are legal from there — and the server
          passes them through untouched precisely so this line can. Replacing it
          with "Something went wrong" would throw away the only part of the
          response an operator can act on. */}
      <div style={{ flex: 1 }}>{message}</div>
      <button type="button" onClick={onDismiss} aria-label="Dismiss error"
        style={{ background: 'none', border: 'none', color: '#7F1D1D', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
        Dismiss
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE STATUS CONTROL
// ═══════════════════════════════════════════════════════════════════

/**
 * What MOVING there does to the recording, in the operator's words.
 *
 * Migration 36 derives `purge_after` from `status` in a BEFORE trigger, so a
 * triage click is also a retention decision — and until this console existed
 * nobody could make that decision at all, which is why the tier had never once
 * fired. Saying it out loud at the moment of the click is the difference between
 * an audited decision and a surprise deletion six months later.
 */
const RETENTION_CONSEQUENCE: Record<TicketStatus, string> = {
  open: '',
  triaged: 'The attached recording is then kept for 365 days from the date the report was filed.',
  in_progress:
    'The attached recording is then kept for 365 days from the date the report was filed. Reopening a resolved ticket also clears its resolution stamp, so a later re-resolution starts a fresh 30 days rather than an already-expired one.',
  resolved:
    'THIS ARMS THE RETENTION CLOCK: the attached recording becomes due for deletion 30 days from now. Reopening the ticket disarms it again.',
  closed:
    'THIS IS TERMINAL AND IT ARMS THE RETENTION CLOCK: the recording becomes due for deletion 30 days from now, and no transition out of `closed` exists. Correcting a mis-close is deliberately a hand-written database job.',
};

/**
 * WHERE A REASON IS INSISTED ON, AND WHOSE RULE THAT IS.
 *
 * Stated plainly because it would otherwise be read as a database constraint:
 * migration 41 requires an ACTOR on every transition (it raises PT400 on a blank
 * one) and NEVER requires a reason — `set_ticket_status` stores
 * `NULLIF(btrim(...), '')` and is perfectly happy with NULL. So this is a
 * CONSOLE policy, and it is drawn where the move is irreversible or destructive:
 * `resolved` and `closed` both arm migration 36's 30-day deletion clock on the
 * recording, and `closed` cannot be left. Requiring a note on a move to
 * `triaged` would just teach people to type "triaged".
 */
function reasonRequiredFor(status: TicketStatus): boolean {
  return status === 'resolved' || status === 'closed';
}

export interface StatusControlProps {
  ticket: SupportTicketDetail;
  /** Re-read the ticket AND the timeline. Called only after a committed write. */
  onChanged: () => void;
}

export function StatusControl({ ticket, onChanged }: StatusControlProps) {
  const { toast } = useToast();
  const [target, setTarget] = useState<TicketStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transitions = ticket.allowed_transitions;

  const apply = useCallback(async (status: TicketStatus, reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await setTicketStatus(ticket.id, status, reason || undefined);
      setTarget(null);

      if (!result.changed) {
        // A documented no-op — the RPC writes no event when the status is
        // re-asserted. Reporting it as a success would put a transition in the
        // operator's head that is not in the audit trail.
        toast({
          kind: 'info',
          title: `${result.ticket_ref} was already ${statusLabel(result.status).toLowerCase()}`,
          body: 'Nothing changed and nothing was written to the timeline.',
        });
      } else {
        toast({
          kind: 'success',
          title: `${result.ticket_ref}: ${statusLabel(result.previous_status)} → ${statusLabel(result.status)}`,
          // The retention consequence as the DATABASE computed it, not as this
          // file predicted it. purge_after comes back from the row after
          // migration 36's trigger ran, so this is the real date.
          body: result.purge_after
            ? `Recording now due for deletion after ${formatDateTime(result.purge_after)}.`
            : 'The recording is not on a deletion clock in this state.',
        });
      }
      onChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The status could not be changed.';
      setError(message);
      toast({ kind: 'error', title: 'Status not changed', body: message });
      // THE DIALOG STAYS OPEN on failure so the typed reason is not lost — the
      // same rule the reporter dialog follows about a failed submit. The message
      // reaches the operator through the TOAST, which renders above the modal;
      // the inline alert behind it is the durable copy, still there (and now
      // reachable) once they close the dialog. Losing a considered triage note
      // to a transient 502 would teach people to write shorter ones.
    } finally {
      setBusy(false);
    }
  }, [ticket.id, onChanged, toast]);

  return (
    <Card>
      <CardHead
        title="Status"
        sub="Every move is validated by the database and appended to the timeline with your name on it."
        right={<StatusPill status={ticket.status} />}
      />
      <div style={{ padding: '14px 18px', display: 'grid', gap: 12 }}>
        {error && <MutationError message={error} onDismiss={() => setError(null)} />}

        {/* The three answers are three different facts — see api.ts. */}
        {transitions == null ? (
          <div role="note" style={noteBox('var(--bg-muted)', 'var(--border)', 'var(--text-secondary)')}>
            <strong>The status control is unavailable.</strong> This server could not read the ticket
            state machine, which means migration 41 has not been applied to its database (or the
            server predates it). Nothing above is affected — the report, the extracts and the replay
            are all complete. Offering buttons anyway would mean guessing which moves the database
            accepts, and every guess that is wrong is a refusal after the click.
          </div>
        ) : transitions.length === 0 ? (
          <div role="note" style={{ ...noteBox('var(--bg-muted)', 'var(--border)', 'var(--text-secondary)'), display: 'flex', gap: 10 }}>
            <Lock size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>This ticket is closed, and closed is terminal.</strong> Closing is what started
              the 30-day clock on the recording, so a ticket that could come back to life might do so
              after its evidence had already been deleted. Correcting a mis-close is a deliberate
              database job — a service-role update plus a hand-written timeline entry — and it is
              meant to feel like one.
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Legal moves from <strong>{statusLabel(ticket.status)}</strong>, as the database defines
              them. Nothing returns to Open: “open” means nobody has looked yet, and the looking has
              already happened.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {transitions.map((next) => {
                const destructive = next === 'closed' || next === 'resolved';
                return (
                  <button
                    key={next}
                    type="button"
                    onClick={() => { setError(null); setTarget(next); }}
                    // Optimistic disable: every control in this card goes down
                    // for the duration of ONE in-flight write, so a double-click
                    // cannot race two transitions against the same row.
                    disabled={busy}
                    style={{
                      ...btnSec,
                      opacity: busy ? 0.5 : 1,
                      cursor: busy ? 'not-allowed' : 'pointer',
                      borderColor: destructive ? '#FCA5A5' : 'var(--border)',
                      color: destructive ? '#B91C1C' : 'var(--text-primary)',
                    }}
                  >
                    {destructive ? <Timer size={13} /> : <CheckCircle2 size={13} />}
                    {/* Ellipsis because it OPENS something — the confirmation
                        carries the consequence and asks for the note. The
                        dialog's own button is the one that commits, and the two
                        deliberately do not read the same. */}
                    Mark {statusLabel(next).toLowerCase()}…
                  </button>
                );
              })}
            </div>
          </>
        )}

      </div>

      <ReasonDialog
        open={target !== null}
        title={target ? `Move ${ticket.ticket_ref} to ${statusLabel(target).toLowerCase()}?` : ''}
        body={target ? RETENTION_CONSEQUENCE[target] : undefined}
        confirmLabel={target ? `Mark ${statusLabel(target).toLowerCase()}` : 'Confirm'}
        danger={target === 'closed' || target === 'resolved'}
        reasonRequired={target ? reasonRequiredFor(target) : false}
        placeholder="Why (appended to this ticket's timeline, under your name)…"
        onConfirm={async (reason) => { if (target) await apply(target, reason); }}
        onCancel={() => setTarget(null)}
      />
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════
// THE GITHUB LOOP, AND THE RE-ARM
// ═══════════════════════════════════════════════════════════════════

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

const dash = <span style={{ color: 'var(--text-muted)' }}>—</span>;

/**
 * Is this a PUBLICATION rather than a retry?
 *
 * `license.portal` is the customer portal: its tickets are the customer's own
 * words, and github-issues.service.ts refuses to auto-file them for that reason.
 * Re-arming one stamps `github_escalated_at`, which is the single flag that
 * makes fileTicket() override its own refusal — so the click does not "retry a
 * sync", it converts the ticket from "will be skipped" into "will be published",
 * permanently. Everything about the control changes when this is true.
 */
function isPublication(ticket: SupportTicketDetail): boolean {
  return ticket.surface_key === 'license.portal';
}

/**
 * Will the DATABASE refuse this re-arm, and why?
 *
 * A MIRROR OF migration 41's gate, not a second implementation of it: the RPC
 * raises PT409 for exactly these cases whatever this function returns, and if
 * the two ever disagree the database wins and the operator sees its message. It
 * exists so the refusal can be explained BEFORE the click instead of arriving
 * as an error afterwards — which for the publication case also means the
 * operator is told what to do (triage it first) rather than merely stopped.
 */
function rearmRefusal(ticket: SupportTicketDetail): string | null {
  if (ticket.github_sync_state === 'synced') {
    return 'This ticket is already filed in GitHub. Re-filing it would mint a second issue for one bug, so the database refuses it.';
  }
  // The gate is keyed on the SURFACE and on 'skipped', in every sync state —
  // deliberately NOT on the sync state alone. A freshly filed portal ticket is
  // 'pending' until the ten-minute sweep reaches it, and on a deployment where
  // the sweep is not running it stays 'pending' forever; a state-keyed check
  // would wave those through.
  const untriaged = ticket.status === 'open';
  if (untriaged && isPublication(ticket)) {
    return 'This is a customer-portal report and it has not been triaged. Publishing a customer’s own words to GitHub is a disclosure, so the database requires that someone has looked at it first: move it out of Open, then escalate.';
  }
  if (untriaged && ticket.github_sync_state === 'skipped') {
    return 'This ticket was skipped rather than filed — that was a decision about publishing it, and re-arming reverses it. Triage the ticket first, then re-arm.';
  }
  return null;
}

export interface GithubCardProps {
  ticket: SupportTicketDetail;
  onChanged: () => void;
}

export function GithubCard({ ticket, onChanged }: GithubCardProps) {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `github_attempts` is NOT NULL DEFAULT 0 in migration 41, so a number here
  // proves the SECTION 6 columns were selected. `undefined` means the licence
  // backend dropped them because its database has not got the migration —
  // "this server cannot tell you", which is not the same as "no" and must not
  // render as a row of zeros and dashes implying a loop that never ran.
  const loopKnown = typeof ticket.github_attempts === 'number';
  const publication = isPublication(ticket);
  const refusal = rearmRefusal(ticket);

  const rearm = useCallback(async (reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await rearmGithubSync(ticket.id, reason || undefined);
      setConfirming(false);
      toast({
        kind: result.escalated ? 'warning' : 'success',
        title: result.escalated
          ? `${result.ticket_ref} released for publication to GitHub`
          : `${result.ticket_ref} queued for GitHub again`,
        // `escalated` means THIS call stamped the permission flag — the console
        // says which of the two things just happened rather than blurring them,
        // because one is a retry and the other is a disclosure.
        body: result.escalated
          ? 'The next sync sweep will file this report as a GitHub issue. That stamp is permanent; it cannot be taken back from here.'
          : `It was ${result.from_state}; the next sweep will try again.`,
      });
      onChanged();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The sync could not be re-armed.';
      setError(message);
      toast({ kind: 'error', title: 'Not re-armed', body: message });
    } finally {
      setBusy(false);
    }
  }, [ticket.id, onChanged, toast]);

  const canRearm = !refusal && !busy;

  return (
    <Card>
      <CardHead
        title="GitHub"
        sub={
          publication
            ? 'Customer-portal reports are never auto-filed. Publishing one is an explicit decision, taken here.'
            : 'Filing, the bounded retry, and the issue’s own state as the loop last read it back.'
        }
        right={
          <GithubSyncChip
            state={ticket.github_sync_state}
            url={ticket.github_issue_url}
            repo={ticket.github_repo}
            issueNumber={ticket.github_issue_number}
            error={ticket.github_sync_error}
            createdAt={ticket.created_at}
          />
        }
      />
      <div style={{ padding: '14px 18px', display: 'grid', gap: 14 }}>
        {error && <MutationError message={error} onDismiss={() => setError(null)} />}

        {!loopKnown ? (
          <div role="note" style={noteBox('var(--bg-muted)', 'var(--border)', 'var(--text-secondary)')}>
            This server did not report the GitHub issue lifecycle — migration 41 has not been applied
            to its database. The sync state above is still accurate; the read-back, the retry budget
            and the escalation stamp simply cannot be shown, and they are not being shown as zeros.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
            <Field label="Issue state">
              {/* NULL is "never read back", which is deliberately NOT 'open' —
                  migration 41's column comment makes that distinction and it is
                  the difference between "the loop says it is open" and "no loop
                  has ever looked". */}
              {ticket.github_issue_state === 'closed' ? (
                <Pill bg="#E9D5FF" fg="#6B21A8"><CheckCircle2 size={10} /> Closed in GitHub</Pill>
              ) : ticket.github_issue_state === 'open' ? (
                <Pill bg="#DCFCE7" fg="#15803D"><GitBranch size={10} /> Open in GitHub</Pill>
              ) : ticket.github_issue_number ? (
                <span style={{ color: 'var(--text-muted)' }}>Never read back</span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>No issue to read</span>
              )}
            </Field>

            <Field label="Resolution">
              {/* The machine reason (completed / not_planned / duplicate), never
                  the closing comment: migration 41 classes that note as customer
                  payload, and the issue itself is one click away for anyone who
                  needs the prose. */}
              {ticket.github_resolution ? (
                <span>
                  {ticket.github_resolution.replace(/_/g, ' ')}
                  {ticket.github_resolution_by && (
                    <span style={{ color: 'var(--text-secondary)' }}> · by {ticket.github_resolution_by}</span>
                  )}
                </span>
              ) : dash}
            </Field>

            <Field label="Closed at">
              {ticket.github_closed_at ? formatDateTime(ticket.github_closed_at) : dash}
            </Field>

            <Field label="Last read back">
              {/* THE POINT OF THIS FIELD: an operator can see the loop working
                  instead of trusting that it is. A timestamp that never advances
                  is how a stopped sweep announces itself. */}
              {ticket.github_state_synced_at ? (
                <span title={formatDateTime(ticket.github_state_synced_at)}>
                  {formatRelativeTime(ticket.github_state_synced_at)}
                </span>
              ) : ticket.github_issue_number ? (
                <span style={{ color: '#C2410C' }}>Never — the read-back loop has not reached this issue</span>
              ) : dash}
            </Field>

            <Field label="Filing attempts">
              {ticket.github_attempts === 0 ? (
                <span style={{ color: 'var(--text-muted)' }}>None failed</span>
              ) : (
                <span>{ticket.github_attempts} failed</span>
              )}
              {ticket.github_last_attempt_at && (
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  last {formatRelativeTime(ticket.github_last_attempt_at)}
                </div>
              )}
            </Field>

            <Field label="Next attempt">
              {ticket.github_next_attempt_at ? (
                // ABSOLUTE, not relative: this one is normally in the FUTURE and
                // formatRelativeTime() only counts backwards — it renders every
                // future instant as "just now", which here would read as "the
                // sweep is about to run" for a retry scheduled next week.
                <span>{formatDateTime(ticket.github_next_attempt_at)}</span>
              ) : (
                // NULL means DO NOT TRY: either it is done, or the retry budget
                // is spent and only a re-arm brings it back. Saying "—" here
                // would hide the one fact that explains a stuck ticket.
                <span style={{ color: 'var(--text-secondary)' }}>
                  Not queued — only a re-arm will try again
                </span>
              )}
            </Field>

            {ticket.github_escalated_at && (
              <Field label="Escalated">
                <span>
                  {formatDateTime(ticket.github_escalated_at)}
                  {ticket.github_escalated_by && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                      by {ticket.github_escalated_by}
                    </div>
                  )}
                </span>
              </Field>
            )}
          </div>
        )}

        {ticket.github_issue_url && (
          <a href={ticket.github_issue_url} target="_blank" rel="noreferrer noopener"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
            <Github size={13} /> Open the issue <ExternalLink size={11} />
          </a>
        )}

        {/* ── THE RE-ARM ──────────────────────────────────────────────────── */}
        {refusal ? (
          <div role="note" style={noteBox('#FEF3C7', '#FDE68A', '#78350F')}>{refusal}</div>
        ) : publication ? (
          // A PUBLICATION CONSENT, and it is styled as one. This is not the same
          // action as retrying a failed sync and it must not look like it: the
          // operator is agreeing to copy a customer's own report into a GitHub
          // issue, which is a disclosure and is not reversible from this console.
          <div style={{ display: 'grid', gap: 10 }}>
            <div role="note" style={{ ...noteBox('#FEE2E2', '#FECACA', '#7F1D1D'), display: 'flex', gap: 10 }}>
              <ShieldAlert size={16} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 12, letterSpacing: '.04em' }}>
                  Escalating publishes customer correspondence
                </div>
                This report was filed by a customer in the portal. Escalating it copies its title and
                body into a GitHub issue in{' '}
                <strong>{ticket.github_repo || 'the owning repository'}</strong>, where anyone with
                access to that repository can read them. The permission stamp is permanent — a later
                change of mind means editing the issue in GitHub, not undoing anything here.
              </div>
            </div>
            <button type="button" onClick={() => { setError(null); setConfirming(true); }} disabled={!canRearm}
              style={{
                ...btnSec, alignSelf: 'flex-start', background: '#DC2626', color: '#fff',
                border: '1px solid #DC2626', opacity: canRearm ? 1 : 0.5,
                cursor: canRearm ? 'pointer' : 'not-allowed',
              }}>
              <ShieldAlert size={13} /> {busy ? 'Escalating…' : 'Escalate to GitHub…'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => { setError(null); setConfirming(true); }} disabled={!canRearm}
              style={{ ...btnSec, opacity: canRearm ? 1 : 0.5, cursor: canRearm ? 'pointer' : 'not-allowed' }}>
              <RefreshCw size={13} /> {busy ? 'Re-arming…' : 'Re-arm GitHub sync'}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 460, lineHeight: 1.5 }}>
              Puts this ticket back in the sweep’s queue and resets its retry budget. Nothing is
              published that was not already approved for publication.
            </span>
          </div>
        )}
      </div>

      <ReasonDialog
        open={confirming}
        title={publication
          ? `Publish ${ticket.ticket_ref} to GitHub?`
          : `Re-arm the GitHub sync for ${ticket.ticket_ref}?`}
        body={publication
          ? 'The report title and body will be copied into a GitHub issue on the next sync sweep. Say why this customer report is being escalated — it is written to the ticket’s timeline under your name, and it is the record of who authorised the disclosure.'
          : 'The ticket returns to the queue with a fresh retry budget. A note is optional but is appended to the timeline under your name.'}
        confirmLabel={publication ? 'Escalate and publish' : 'Re-arm'}
        danger={publication}
        // Same policy line as the status control: the database never demands a
        // reason, the console demands one where the act is a disclosure.
        reasonRequired={publication}
        placeholder="Why (appended to this ticket's timeline, under your name)…"
        onConfirm={rearm}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
